import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getVisitAPI, updateVisitorAPI, alreadyDecidedText, isAlreadyDecided } from '../api/VisitorAPI';
import OptimizedImage from './common/OptimizedImage';
import { User, Phone } from './common/Icon';
import { COLORS, GREY, SHADOWS, RADII, FONT_SIZES, FONT_WEIGHTS } from '../styles/theme';
import { formatMobile } from '../utils/dialer';
import { nativeBridge } from '../utils/NativeBridge';
import { getUserType } from '../utils/cachedUser';
import { visitorSheetBus } from '../utils/visitorSheetBus';
import { getGuardRingBannerContent } from '../constants/pushBanner';
import { flatLabel, formatVisitTime, inOutText, VISITOR_TONES } from '../constants/visitorStatus';

// Roles that may answer a ring in the popup. Anything else — including an empty or
// unknown type (the user cache is blank after a society switch until GET /v1/user
// rewrites it, and stays blank if that fails) — is not known to be a resident, so it
// gets the banner instead; a guard must never get the resident popup (Rule 3).
const POPUP_ROLES = ['RESIDENTIAL', 'SOCIETY_ADMIN'];

interface VisitorInfo {
  visitorId: string;
  name: string;
  mobile: string;
  type: string;
  status: string;
  photo?: string | null;
  publicPhoto?: string | null;
  visitId?: number;
  approvedByName?: string;
  wingName?: string;
  homeNo?: string;
  enteredAt?: number;
  exitedAt?: number;
}

type FailedAction = { status: 'APPROVED' | 'REJECTED'; network: boolean; message?: string };

const GlobalVisitorApprovalOverlay: React.FC = () => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [visitorUuid, setVisitorUuid] = useState<string | null>(null);
  const [visitId, setVisitId] = useState<number | null>(null);
  const visitIdRef = useRef<number | null>(null);
  const [visitor, setVisitor] = useState<VisitorInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionDone, setActionDone] = useState<'APPROVED' | 'REJECTED' | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Popup state 6: the answer didn't reach the server; buttons come back with "Try again".
  const [failed, setFailed] = useState<FailedAction | null>(null);
  // Server text of the 409 that told us someone else answered first.
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);
  // Which answer is in flight, so only that button reads "Approving…" / "Denying…".
  const [pendingAction, setPendingAction] = useState<'APPROVED' | 'REJECTED' | null>(null);

  // Visitors already shown/dismissed/actioned this session — the native side
  // (firebase_service.dart's foreground branch) dispatches this event
  // straight from onMessage without messageId dedup, since FCM can on rare
  // occasions redeliver the same foreground message. Without this guard a
  // redelivery reopens the overlay right after the user closed or actioned
  // it. Bounded so a long session can't grow this unboundedly.
  const handledUuidsRef = useRef<string[]>([]);
  // Visits that already got the neutral "role unknown" banner (kept apart from
  // handledUuidsRef so a later reminder can still open the popup).
  const unknownRoleBannerRef = useRef<Set<string>>(new Set());
  // Uuid the overlay is currently showing. The backend re-sends the push while a
  // visitor is still pending; a repeat for the same visitor just re-chimes — it
  // must not reset the open overlay back to its loading state.
  // Both refs below hold a per-VISIT key (`uuid:visitId`, or bare uuid when the push has no
  // visitId) — one person can have many visits, so the person uuid alone would suppress a new visit.
  const shownUuidRef = useRef<string | null>(null);
  const currentKeyRef = useRef<string | null>(null);
  const markHandled = useCallback((uuid: string) => {
    if (handledUuidsRef.current.includes(uuid)) return;
    handledUuidsRef.current.push(uuid);
    if (handledUuidsRef.current.length > 50) handledUuidsRef.current.shift();
  }, []);

  // Listen for the foreground visitor approval event
  useEffect(() => {
    const handler = (e: Event) => {
      const extra = (e as CustomEvent).detail?.extra;
      const uuid = extra?.visitorUuid;
      const pushVisitId = Number(extra?.visitId);
      const newVisitId = Number.isFinite(pushVisitId) && pushVisitId > 0 ? pushVisitId : null;
      if (!uuid) {
        // Stop ring if no uuid provided (malformed push)
        nativeBridge.stopVisitorRing();
        return;
      }
      const key = newVisitId ? `${uuid}:${newVisitId}` : uuid;
      const userType = getUserType();
      // Rule 3: an account signed in as a guard never gets the resident popup, even for a
      // home it also belongs to. One banner per visit: reminders are marked handled.
      if (userType === 'GUARD') {
        nativeBridge.stopVisitorRing();
        if (handledUuidsRef.current.includes(key)) return;
        markHandled(key);
        const { title, body } = getGuardRingBannerContent(extra);
        try {
          window.dispatchEvent(new CustomEvent('inAppPushBanner', { detail: { title, body, deepLink: '/visitor', extra } }));
        } catch (_) {}
        return;
      }
      // Role not known yet (user cache blank after login / a society switch until
      // GET /v1/user lands): no popup, but never silence a possibly-resident ring. Neutral
      // banner once per visit; keep ringing and leave handledUuids alone, so the next
      // reminder opens the popup once the role is known.
      if (!POPUP_ROLES.includes(userType)) {
        if (unknownRoleBannerRef.current.has(key)) return;
        unknownRoleBannerRef.current.add(key);
        if (unknownRoleBannerRef.current.size > 50) {
          const first = unknownRoleBannerRef.current.values().next().value;
          if (first !== undefined) unknownRoleBannerRef.current.delete(first);
        }
        const name = extra?.visitorName || extra?.name || 'A visitor';
        try {
          window.dispatchEvent(new CustomEvent('inAppPushBanner', {
            detail: { title: `${name} is waiting at the gate`, body: 'Tap to answer', deepLink: '/visitor', extra },
          }));
        } catch (_) {}
        return;
      }
      // Rule 1: the ring is for the visit already open in a details sheet. That sheet
      // shows the Approve/Deny bar in place; refresh it instead of stacking a popup.
      if (visitorSheetBus.isOpen({ visitId: newVisitId, visitorUuid: uuid })) {
        visitorSheetBus.refresh({ visitId: newVisitId, visitorUuid: uuid });
        return;
      }
      if (handledUuidsRef.current.includes(key)) {
        // Stop ring if we've already handled this visitor (redelivered push)
        nativeBridge.stopVisitorRing();
        return;
      }
      if (shownUuidRef.current === key) {
        // Repeat push for the open overlay: refresh silently (another family
        // member may have answered) without resetting to the loading state.
        if (newVisitId) { visitIdRef.current = newVisitId; setVisitId(newVisitId); }
        getVisitAPI(navigate, uuid, newVisitId ?? visitIdRef.current).then((data) => {
          if (shownUuidRef.current === key && data) setVisitor(data);
        }).catch(() => {});
        return;
      }
      // Rule 2: a different visit's sheet is open; it closes and the popup takes its place.
      if (visitorSheetBus.openVisit) visitorSheetBus.close();
      shownUuidRef.current = key;
      currentKeyRef.current = key;
      visitIdRef.current = newVisitId;
      setVisitId(newVisitId);
      setVisitorUuid(uuid);
      setVisitor(null);
      setActionDone(null);
      setError(null);
      setFailed(null);
      setConflictMessage(null);
      setVisible(true);
    };
    window.addEventListener('visitorApprovalRequired', handler);
    return () => window.removeEventListener('visitorApprovalRequired', handler);
  }, [navigate, markHandled]);

  // Fetch visitor details when overlay opens
  useEffect(() => {
    if (!visible || !visitorUuid) return;
    let cancelled = false;
    setLoading(true);
    getVisitAPI(navigate, visitorUuid, visitId).then((data) => {
      if (!cancelled) {
        setVisitor(data ?? null);
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) { setError('Could not load visitor details.'); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [visible, visitorUuid, visitId, navigate]);

  // While still pending, poll quietly: the backend stops re-sending the push once
  // anyone answers, so a family member's decision would otherwise never show here.
  const visitorStatus = visitor?.status;
  useEffect(() => {
    if (!visible || !visitorUuid || actionDone || actionLoading) return;
    if (visitorStatus !== 'COMING' && visitorStatus !== 'GUARD') return;
    const pollKey = currentKeyRef.current;
    const interval = setInterval(() => {
      if (document.hidden) return;
      getVisitAPI(navigate, visitorUuid, visitIdRef.current ?? visitor?.visitId).then((data) => {
        if (shownUuidRef.current !== pollKey || !data) return;
        if (data.visitorId && visitor?.visitorId && data.visitorId !== visitor.visitorId) return;
        // Someone answered (or the guard cancelled): nothing left to ring for.
        if (data.status !== 'COMING' && data.status !== 'GUARD') nativeBridge.stopVisitorRing();
        setVisitor(data);
      }).catch(() => {});
    }, 4000);
    return () => clearInterval(interval);
  }, [visible, visitorUuid, visitorStatus, visitor?.visitorId, visitor?.visitId, actionDone, actionLoading, navigate]);

  // Auto-close after action
  useEffect(() => {
    if (!actionDone) return;
    const t = setTimeout(() => setVisible(false), 2000);
    return () => clearTimeout(t);
  }, [actionDone]);

  // Silence the native ring whenever the overlay goes away (closed, auto-closed
  // after an action, or unmounted while visible). Idempotent on the native side.
  useEffect(() => {
    if (!visible) return;
    return () => {
      shownUuidRef.current = null;
      nativeBridge.stopVisitorRing();
    };
  }, [visible]);

  const handleDismiss = useCallback(() => {
    nativeBridge.stopVisitorRing();
    if (currentKeyRef.current) markHandled(currentKeyRef.current);
    setVisible(false);
  }, [markHandled]);

  const handleAction = useCallback(async (status: 'APPROVED' | 'REJECTED') => {
    if (!visitor?.visitorId || actionLoading) return;
    nativeBridge.stopVisitorRing();
    setActionLoading(true);
    setPendingAction(status);
    setError(null);
    setFailed(null);
    try {
      const vid = visitIdRef.current ?? visitor.visitId;
      const response = await updateVisitorAPI(navigate, {
        visitorId: visitor.visitorId, status, ...(vid ? { visitId: vid } : {}),
      });
      if (response?.success) {
        setActionDone(status);
        if (currentKeyRef.current) markHandled(currentKeyRef.current);
      } else if (isAlreadyDecided(response) || /no longer pending/i.test(response?.message || '')) {
        // Someone else decided first (or the guard cancelled): show that, not an error.
        const fresh = visitorUuid ? await getVisitAPI(navigate, visitorUuid, vid).catch(() => null) : null;
        if (fresh) setVisitor(fresh);
        else setVisitor(prev => prev ? { ...prev, status: 'DECIDED' } : prev);
        setConflictMessage(response?.message || null);
        if (currentKeyRef.current) markHandled(currentKeyRef.current);
      } else {
        // A status of 0/undefined means the request never reached the server.
        setFailed({ status, network: !response?.status, message: response?.message });
      }
    } catch {
      setFailed({ status, network: true });
    } finally {
      setActionLoading(false);
    }
  }, [visitor?.visitorId, visitor?.visitId, actionLoading, navigate, visitorUuid, markHandled]);

  if (!visible) return null;

  const needsApproval = visitor && (visitor.status === 'COMING' || visitor.status === 'GUARD');
  const name = visitor?.name || 'The visitor';
  const flat = flatLabel(visitor);

  // Popup states 3/4 (this user answered), 5/7 (already answered / cancelled), 6 (failed).
  const resultBox = (big: string, small: string, tone: 'ok' | 'den' | 'neutral' | 'err') => {
    const palette = {
      ok: { color: COLORS.success, bg: COLORS.successBg },
      den: { color: COLORS.errorDark, bg: COLORS.errorBg },
      neutral: { color: GREY[700], bg: GREY[100] },
      err: { color: COLORS.warningText, bg: COLORS.warningBg },
    }[tone];
    return (
      <div role="status" style={{
        borderRadius: RADII.xl, padding: '14px 12px', textAlign: 'center',
        background: palette.bg, color: palette.color, marginBottom: 12,
      }}>
        <div style={{ fontWeight: FONT_WEIGHTS.heavy, fontSize: FONT_SIZES.lg }}>{big}</div>
        {small && <div style={{ fontSize: FONT_SIZES.sm, marginTop: 3, opacity: 0.85 }}>{small}</div>}
      </div>
    );
  };

  const decidedView = () => {
    if (!visitor) return null;
    const st = visitor.status;
    const big = alreadyDecidedText(visitor, conflictMessage || undefined);
    let small = '';
    if (st === 'ENTERED') small = visitor.enteredAt ? `${name} is inside since ${formatVisitTime(visitor.enteredAt)}` : `${name} is inside`;
    else if (st === 'APPROVED' || st === 'GUARD') small = `${name} can enter`;
    else if (st === 'REJECTED') small = `Guard has been told not to let ${name} in`;
    else if (st === 'EXITED') small = inOutText(visitor.enteredAt, visitor.exitedAt);
    else if (st === 'DELETED' || st === 'CANCELLED') small = 'Nothing to do';
    return resultBox(big, small, 'neutral');
  };

  const btn = (kind: 'deny' | 'approve' | 'ok', label: string, onClick: () => void, disabled = false) => {
    const look = disabled
      ? { background: GREY[100], color: GREY[400] }
      : kind === 'deny' ? { background: VISITOR_TONES.den.bg, color: COLORS.errorDark }
      : kind === 'approve' ? { background: COLORS.textPrimary, color: COLORS.white }
      : { background: COLORS.white, color: GREY[900], border: `1.5px solid ${GREY[200]}` };
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        style={{
          flex: 1, padding: '12px', border: 'none', borderRadius: RADII.xl,
          fontWeight: FONT_WEIGHTS.heavy, fontSize: FONT_SIZES.md,
          cursor: disabled ? 'not-allowed' : 'pointer', ...look,
        }}
      >{label}</button>
    );
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      // This can pop open on ANY page, at any time, from a push-notification event —
      // it can never assume what else is currently open, so it must outrank every
      // other overlay in the app rather than just the ones known to collide with it
      // today. 10000 clears the app's own 9999-tier ceiling (DeleteConfirmationModal,
      // camera.tsx, JoinSocietyForm's cropper, etc.) as well as the 3000-tier modals
      // it used to tie with (VisitorPickerSheet, VisitorDetailsModal's confirm dialog)
      // and lose to, since it's mounted earliest in the DOM (App.tsx, before Routes).
      zIndex: 10000,
    }}>
      <div style={{
        backgroundColor: COLORS.white,
        borderRadius: '20px',
        padding: '20px',
        width: '90%',
        maxWidth: '360px',
        boxShadow: SHADOWS.elevated,
        border: '1px solid rgba(0,0,0,0.06)',
        position: 'relative',
      }}>
        {/* Close button */}
        <button onClick={handleDismiss} aria-label="Close" style={{
          position: 'absolute', top: '12px', right: '12px',
          background: GREY[100], border: 'none',
          fontSize: '16px', cursor: 'pointer', width: '28px', height: '28px',
          borderRadius: '50%', display: 'flex', alignItems: 'center',
          justifyContent: 'center', color: GREY[500], zIndex: 1,
        }}>×</button>

        {/* Header label */}
        <div style={{
          fontSize: FONT_SIZES.xs, fontWeight: FONT_WEIGHTS.heavy, color: VISITOR_TONES.wait.color,
          textTransform: 'uppercase', letterSpacing: '0.8px',
          marginBottom: '16px', paddingRight: 36,
        }}>{flat ? `Visitor at gate · ${flat}` : 'Visitor at gate'}</div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: GREY[500], fontSize: FONT_SIZES.md }}>
            Loading visitor details…
          </div>
        )}

        {error && !loading && (
          <div style={{ textAlign: 'center', padding: '20px 0', color: COLORS.error, fontSize: FONT_SIZES.md }}>
            {error}
          </div>
        )}

        {!loading && !error && visitor && (
          <>
            {/* Visitor identity */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
              <OptimizedImage
                src={visitor.publicPhoto || visitor.photo}
                style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${GREY[100]}`, flexShrink: 0 }}
                fallbackIcon={<User size={22} color={GREY[500]} />}
              />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '17px', fontWeight: FONT_WEIGHTS.bold, color: COLORS.textPrimary, marginBottom: '2px' }}>
                  {visitor.name}
                </div>
                {visitor.mobile && (
                  <a href={`tel:${visitor.mobile}`} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    textDecoration: 'none', color: '#007AFF', fontSize: FONT_SIZES.base,
                  }}>
                    <Phone size={12} strokeWidth={2} />
                    {formatMobile(visitor.mobile)}
                  </a>
                )}
                {visitor.type && (
                  <div style={{
                    display: 'inline-block', marginTop: '4px', marginLeft: visitor.mobile ? 6 : 0,
                    fontSize: FONT_SIZES.xs, fontWeight: FONT_WEIGHTS.semibold, color: VISITOR_TONES.wait.color,
                    background: VISITOR_TONES.wait.bg, padding: '1px 8px', borderRadius: '99px',
                  }}>
                    {visitor.type.replace('_', ' ')}
                  </div>
                )}
              </div>
            </div>

            {actionDone === 'APPROVED' ? (
              resultBox(`✓ Approved — ${name} can enter`, 'Guard has been told', 'ok')
            ) : actionDone === 'REJECTED' ? (
              resultBox('✕ Entry denied', `Guard has been told not to let ${name} in`, 'den')
            ) : needsApproval ? (
              <>
                {failed && resultBox(
                  "Couldn't send your answer",
                  failed.network || !failed.message ? 'Check your connection and try again' : failed.message,
                  'err',
                )}
                <div style={{ display: 'flex', gap: '10px' }}>
                  {btn('deny',
                    actionLoading ? (pendingAction === 'REJECTED' ? 'Denying…' : 'Deny') : failed?.status === 'REJECTED' ? 'Try again' : 'Deny',
                    () => handleAction('REJECTED'), actionLoading)}
                  {btn('approve',
                    actionLoading ? (pendingAction === 'APPROVED' ? 'Approving…' : 'Approve') : failed?.status === 'APPROVED' ? 'Try again' : 'Approve',
                    () => handleAction('APPROVED'), actionLoading)}
                </div>
                {!failed && (
                  <div style={{ fontSize: FONT_SIZES.sm, color: GREY[500], textAlign: 'center', marginTop: 10 }}>
                    Guard is holding {name} at the gate
                  </div>
                )}
              </>
            ) : (
              <>
                {decidedView()}
                <div style={{ display: 'flex' }}>{btn('ok', 'OK', handleDismiss)}</div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GlobalVisitorApprovalOverlay;
